import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { taskApi } from 'app/services/api';
import Skeleton from 'app/shared/components/common/Skeleton';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { SKELETON_TYPES } from 'constants/skeleton';
import { TaskHistoryAudience, THREAD_ENTRY_KINDS } from 'constants/tasks';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import TaskThreadComposer from './components/TaskThreadComposer';
import TaskThreadEntry from './components/TaskThreadEntry';
import {
  TaskThreadTagType,
  THREAD_EMPTY_MESSAGE,
  THREAD_LOAD_ERROR_MESSAGE,
} from './TaskThread.constants';
import { emptyStyles, threadListStyles, threadWrapperStyles } from './TaskThread.styles';
import { TaskThreadTestIds } from './TaskThread.testIds';

const defaultFetchHistory = params => taskApi.getTaskHistory(params);

// Mounts for internal sessions only until the staff history read is audience-scoped
// server-side (DEV-6641); the canViewAllAudiences filter is defense-in-depth for reuse.
const TaskThread = ({
  taskId,
  canPost = false,
  canChooseAudience = false,
  canViewAllAudiences = false,
  entryKinds = THREAD_ENTRY_KINDS,
  tagType = TaskThreadTagType.AUDIENCE,
  fetchHistory = defaultFetchHistory,
}) => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  const orderedEntries = useMemo(() => {
    const threadEntries = entries.filter(({ entryKind }) => entryKinds.includes(entryKind));

    const visibleEntries = canViewAllAudiences
      ? threadEntries
      : threadEntries.filter(({ audience }) => audience === TaskHistoryAudience.TO_PARTNER);

    return _.orderBy(visibleEntries, [entry => new Date(entry.eventDate)], ['asc']);
  }, [entries, canViewAllAudiences, entryKinds]);

  // Composer refreshes call this with no argument, so a post never blanks the thread
  // behind a skeleton — only the initial load does.
  const loadHistory = useCallback(
    (withSkeleton = false) => {
      if (withSkeleton) {
        setIsLoading(true);
      }

      setHasLoadError(false);

      return fetchHistory({ taskId })
        .then(({ data }) => setEntries(data || []))
        .catch(() => setHasLoadError(true))
        .finally(() => setIsLoading(false));
    },
    [taskId, fetchHistory],
  );

  useEffectSafeStrictMode(() => {
    loadHistory(true);
  }, [loadHistory]);

  if (isLoading) {
    return <Skeleton cellsNumber={1} length={4} type={SKELETON_TYPES.TABLE} />;
  }

  return (
    <Box data-testid={TaskThreadTestIds.THREAD} sx={threadWrapperStyles}>
      {hasLoadError && (
        <Box data-testid={TaskThreadTestIds.EMPTY} sx={emptyStyles}>
          <Typography color="error" variant="regular14">
            {THREAD_LOAD_ERROR_MESSAGE}
          </Typography>
        </Box>
      )}

      {orderedEntries.length === 0 ? (
        !hasLoadError && (
          <Box data-testid={TaskThreadTestIds.EMPTY} sx={emptyStyles}>
            <Typography color="text.secondary" variant="regular14">
              {THREAD_EMPTY_MESSAGE}
            </Typography>
          </Box>
        )
      ) : (
        <Box sx={threadListStyles}>
          {orderedEntries.map((entry, index) => (
            <TaskThreadEntry
              {...entry}
              key={entry.taskHistoryRecordId ?? `${entry.eventDate}-${index}`}
              tagType={tagType}
            />
          ))}
        </Box>
      )}

      {canPost && (
        <TaskThreadComposer
          canChooseAudience={canChooseAudience}
          taskId={taskId}
          onPosted={loadHistory}
        />
      )}
    </Box>
  );
};

TaskThread.propTypes = {
  taskId: PropTypes.string.isRequired,
  canPost: PropTypes.bool,
  canChooseAudience: PropTypes.bool,
  canViewAllAudiences: PropTypes.bool,
  entryKinds: PropTypes.arrayOf(PropTypes.string),
  fetchHistory: PropTypes.func,
  tagType: PropTypes.oneOf(Object.values(TaskThreadTagType)),
};

export default memo(TaskThread);
